import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/* One Euro Filter */
class OneEuroFilter {
  constructor(freq = 180, minCutoff = 1.0, beta = 0.02, dCutoff = 1.0) {
    this.freq = freq;
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.dCutoff = dCutoff;
    this.xPrev = null;
    this.dxPrev = 0;
    this.tPrev = null;
  }
  alpha(cutoff) {
    const te = 1.0 / this.freq;
    const tau = 1.0 / (2 * Math.PI * cutoff);
    return 1.0 / (1.0 + tau / te);
  }
  filter(t, x) {
    if (this.tPrev == null) {
      this.tPrev = t;
      this.xPrev = x;
      return x;
    }
    const te = Math.max(1e-4, (t - this.tPrev) / 1000.0);
    this.freq = 1.0 / te;

    const dx = (x - this.xPrev) * this.freq;
    const aD = this.alpha(this.dCutoff);
    const dxHat = aD * dx + (1 - aD) * this.dxPrev;

    const cutoff = this.minCutoff + this.beta * Math.abs(dxHat);
    const a = this.alpha(cutoff);

    const xHat = a * x + (1 - a) * this.xPrev;
    this.xPrev = xHat;
    this.dxPrev = dxHat;
    this.tPrev = t;
    return xHat;
  }
}

const RegistrarFirmaInstructor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  const [drawing, setDrawing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  const [mensaje, setMensaje] = useState("");

  const MAX_DESKTOP_WIDTH = 700;
  const DESKTOP_HEIGHT = 260;

  // Si quieres dejarlo EXACTO al Aprendiz, usa oversample = 1
  // Si quieres tu calidad extra, deja 1.5
  const oversample = 1.5;

  const lastPosRef = useRef(null);
  const fxRef = useRef(null);
  const fyRef = useRef(null);

  /* === BBox + recorte === */
  const getStrokeBoundingBox = (canvas, alphaThreshold = 8) => {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const { width, height } = canvas;
    const { data } = ctx.getImageData(0, 0, width, height);

    let top = height, left = width, right = -1, bottom = -1;

    for (let y = 0; y < height; y++) {
      const rowStart = y * width * 4;
      for (let x = 0; x < width; x++) {
        const idx = rowStart + x * 4;
        const a = data[idx + 3];
        if (a >= alphaThreshold) {
          if (x < left) left = x;
          if (x > right) right = x;
          if (y < top) top = y;
          if (y > bottom) bottom = y;
        }
      }
    }

    if (right === -1) return null;
    return { left, top, right, bottom };
  };

  const getCroppedSignatureBlob = async (
    canvas,
    { padding = 22, normalizeDPR = true, mime = "image/png", quality = 0.92, alphaThreshold = 8 } = {}
  ) => {
    const bbox = getStrokeBoundingBox(canvas, alphaThreshold);
    if (!bbox) return null;

    const dpr = window.devicePixelRatio || 1;

    const srcX = Math.max(0, bbox.left - padding);
    const srcY = Math.max(0, bbox.top - padding);
    const srcW = Math.min(canvas.width - srcX, (bbox.right - bbox.left + 1) + padding * 2);
    const srcH = Math.min(canvas.height - srcY, (bbox.bottom - bbox.top + 1) + padding * 2);

    const outW = normalizeDPR ? Math.max(1, Math.round(srcW / dpr)) : srcW;
    const outH = normalizeDPR ? Math.max(1, Math.round(srcH / dpr)) : srcH;

    const off = document.createElement("canvas");
    off.width = outW;
    off.height = outH;

    const offCtx = off.getContext("2d");
    offCtx.imageSmoothingEnabled = true;
    offCtx.imageSmoothingQuality = "high";

    if (normalizeDPR) offCtx.drawImage(canvas, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
    else offCtx.drawImage(canvas, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

    return await new Promise((resolve) => off.toBlob(resolve, mime, quality));
  };

  /* === Responsive + DPR oversampling === */
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { alpha: true, willReadFrequently: true });

    const resizeCanvas = () => {
      const ratio = (window.devicePixelRatio || 1) * oversample;

      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsPortrait(window.innerHeight > window.innerWidth);

      if (mobile) {
        const width = window.innerWidth;
        const height = Math.floor(window.innerHeight * 0.75);
        canvas.width = Math.max(1, Math.floor(width * ratio));
        canvas.height = Math.max(1, Math.floor(height * ratio));
        canvas.style.width = width + "px";
        canvas.style.height = height + "px";
      } else {
        const parentWidth = canvas.parentElement?.offsetWidth || MAX_DESKTOP_WIDTH;
        const visibleWidth = Math.min(parentWidth * 0.9, MAX_DESKTOP_WIDTH);
        const visibleHeight = DESKTOP_HEIGHT;

        canvas.width = Math.max(1, Math.floor(visibleWidth * ratio));
        canvas.height = Math.max(1, Math.floor(visibleHeight * ratio));

        canvas.style.width = `${visibleWidth}px`;
        canvas.style.height = `${visibleHeight}px`;
      }

      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#000";
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, []);

  /* === Dibujo (ahora: mouse + touch + pen, como Aprendiz) === */
  const pressureCurve = (p) => {
    const minW = 1.2, maxW = 4.6;
    const curved = Math.pow(p || 0.5, 0.65);
    return minW + (maxW - minW) * curved;
  };

  const startPointer = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrawing(true);

    const ctx = canvasRef.current.getContext("2d");
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Para touch/mouse normalmente pressure viene 0 o 0.5
    const p = e.pressure && e.pressure > 0 ? e.pressure : 0.5;
    const t = e.timeStamp || performance.now();

    fxRef.current = new OneEuroFilter(180, 1.0, 0.02, 1.0);
    fyRef.current = new OneEuroFilter(180, 1.0, 0.02, 1.0);

    const sx = fxRef.current.filter(t, x);
    const sy = fyRef.current.filter(t, y);

    ctx.globalCompositeOperation = "source-over";
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineWidth = pressureCurve(p);

    lastPosRef.current = { x: sx, y: sy, t, p };
  };

  const movePointer = (e) => {
    if (!drawing) return;
    e.preventDefault();

    const ctx = canvasRef.current.getContext("2d");
    const events = e.getCoalescedEvents ? e.getCoalescedEvents() : [e];

    events.forEach((ev) => {
      const rect = canvasRef.current.getBoundingClientRect();
      const rawX = ev.clientX - rect.left;
      const rawY = ev.clientY - rect.top;

      const p = ev.pressure && ev.pressure > 0 ? ev.pressure : 0.5;
      const t = ev.timeStamp || performance.now();

      const sx = fxRef.current ? fxRef.current.filter(t, rawX) : rawX;
      const sy = fyRef.current ? fyRef.current.filter(t, rawY) : rawY;

      const last = lastPosRef.current;
      if (!last) {
        lastPosRef.current = { x: sx, y: sy, t, p };
        return;
      }

      const dt = Math.max(1e-4, (t - last.t) / 1000);
      const vx = (sx - last.x) / dt;
      const vy = (sy - last.y) / dt;
      const speed = Math.hypot(vx, vy);
      const speedFactor = 1 / (1 + 0.002 * speed);

      ctx.lineWidth = pressureCurve((p + last.p) / 2) * speedFactor;

      const dx = sx - last.x;
      const dy = sy - last.y;
      if (dx * dx + dy * dy < 0.12 * 0.12) return;

      ctx.lineTo(sx, sy);
      ctx.stroke();

      lastPosRef.current = { x: sx, y: sy, t, p };
    });
  };

  const endPointer = (e) => {
    if (!drawing) return;
    e.preventDefault();
    setDrawing(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    lastPosRef.current = null;
  };

  const cancelPointer = () => {
    if (!drawing) return;
    setDrawing(false);
    lastPosRef.current = null;
  };

  /* === Acciones === */
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    lastPosRef.current = null;
    setMensaje("");
  };

  const saveSignature = async () => {
    const canvas = canvasRef.current;

    try {
      const blob = await getCroppedSignatureBlob(canvas, {
        padding: 22,
        normalizeDPR: true,
        mime: "image/png",
        quality: 0.92,
        alphaThreshold: 8,
      });

      if (!blob) {
        setMensaje("⚠️ No hay firma dibujada.");
        return;
      }

      const file = new File([blob], `firmaInstructor-${id}.png`, { type: "image/png" });
      const formData = new FormData();
      formData.append("firmaInstructor", file);

      await axios.put(
        `/api/instructores/update/firma-instructor/${id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setMensaje("✅ Firma guardada con éxito");
      setTimeout(() => navigate(-1), 1500);
    } catch (error) {
      const msg =
        (typeof error.response?.data === "string" && error.response.data) ||
        error.response?.data?.message ||
        error.message ||
        "Error al guardar la firma";
      setMensaje(`❌ ${msg}`);
    }
  };

  // Evitar menú contextual (click derecho)
  useEffect(() => {
    const canvas = canvasRef.current;
    const preventContext = (e) => e.preventDefault();
    canvas.addEventListener("contextmenu", preventContext);
    return () => canvas.removeEventListener("contextmenu", preventContext);
  }, []);

  return (
    <div
      className={isMobile ? "" : "card shadow p-3 mb-3"}
      style={{
        height: isMobile ? "100vh" : "auto",
        width: isMobile ? "100vw" : "auto",
        position: isMobile ? "fixed" : "static",
        top: isMobile ? 0 : "auto",
        left: isMobile ? 0 : "auto",
        background: "#fff",
        zIndex: 1050,
        overflow: "hidden",
      }}
    >
      {!isMobile && (
        <h5 className="text-center mb-3">✍️ Registrar Firma del Instructor (ID: {id})</h5>
      )}

      {isMobile && isPortrait && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.8)",
            color: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            zIndex: 2000,
            textAlign: "center",
            padding: "20px",
          }}
        >
          <h3>🔄 Rota tu dispositivo</h3>
          <p>Por favor coloca el dispositivo en posición horizontal para firmar.</p>
        </div>
      )}

      <div className="d-flex justify-content-center">
        <div style={{ width: "100%", maxWidth: `${MAX_DESKTOP_WIDTH}px` }}>
          <canvas
            ref={canvasRef}
            className="border border-dark rounded"
            style={{
              backgroundColor: "#fff",
              cursor: "crosshair",
              touchAction: "none",
              display: "block",
              margin: "0 auto",
            }}
            onPointerDown={startPointer}
            onPointerMove={movePointer}
            onPointerUp={endPointer}
            onPointerCancel={cancelPointer}
            onPointerOut={cancelPointer}
            onLostPointerCapture={cancelPointer}
          />
        </div>
      </div>

      {/* Botonera igual al Aprendiz: fija en móvil, normal en desktop */}
      <div
        className="d-flex justify-content-between"
        style={{
          position: isMobile ? "fixed" : "static",
          bottom: isMobile ? "15px" : "auto",
          left: isMobile ? "10px" : "auto",
          right: isMobile ? "10px" : "auto",
          background: isMobile ? "rgba(255,255,255,0.95)" : "none",
          padding: isMobile ? "6px" : "0",
          borderRadius: "12px",
          boxShadow: isMobile ? "0px 2px 6px rgba(0,0,0,0.2)" : "none",
          marginTop: "15px",
          gap: "8px",
          maxWidth: isMobile ? "unset" : `${MAX_DESKTOP_WIDTH}px`,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <button type="button" className="btn btn-secondary" onClick={clearCanvas}>
          Limpiar
        </button>
        <button type="button" className="btn btn-success" onClick={saveSignature}>
          Guardar Firma
        </button>
      </div>

      {mensaje && (
        <p
          className="text-center mt-3"
          style={{
            color: mensaje.startsWith("✅") ? "green" : mensaje.startsWith("⚠️") ? "#b58900" : "crimson",
            fontWeight: "bold",
          }}
        >
          {mensaje}
        </p>
      )}
    </div>
  );
};

export default RegistrarFirmaInstructor;